// ContractorListingPage.tsx - Updated with 2-digit state code requirement and location-based filtering
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useLoaderData } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Alert, Badge, Button } from 'reactstrap';
import { useContractors } from '../../hooks/useContractors';
import { useContractorLocations } from '../../hooks/useContractorLocations';
import Header from '../Home/Header';
import Footer from '../Home/Footer';
import SEOTags from './SEOTags';
import ContractorListingGrid from './ContractorListingGrid';
import ListingPagination from './ListingPagination';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';

interface UserLocation {
  city: string;
  state: string; // This will be the 2-digit state code
  stateCode: string; // 2-digit code
  latitude?: number;
  longitude?: number;
}

interface CityLocation {
  name: string;
  state: string;
  stateCode: string;
  count: number;
}

interface ListingPageLoaderData {
  state?: string; // 2-digit state code from URL
}

// State name to 2-digit code mapping
const STATE_CODE_MAP: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
};

// Reverse mapping for display
const STATE_NAME_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_CODE_MAP).map(([name, code]) => [code, name])
);

const ContractorListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const loaderData = useLoaderData() as ListingPageLoaderData;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTitle, setPageTitle] = useState('Contractors Directory');
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [topCityLocations, setTopCityLocations] = useState<CityLocation[]>([]);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

  const itemsPerPage = 12;

  // Get location data for the sidebar
  const { locations } = useContractorLocations();

  // Convert state name to 2-digit code
  const getStateCode = (stateName: string): string => {
    const cleanName = stateName.toLowerCase().trim();
    return STATE_CODE_MAP[cleanName] || stateName.toUpperCase().substring(0, 2);
  };

  // Convert 2-digit code to state name
  const getStateName = (stateCode: string): string => {
    return STATE_NAME_MAP[stateCode.toUpperCase()] || stateCode;
  };

  // Detect user location and convert to 2-digit state code
  useEffect(() => {
    const detectUserLocation = async () => {
      setLocationLoading(true);
      setLocationError(null);
      
      try {
        // Try browser geolocation first
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              
              try {
                // Reverse geocoding to get city/state from coordinates
                const location = await reverseGeocode(latitude, longitude);
                const stateCode = getStateCode(location.state);
                
                setUserLocation({
                  city: location.city,
                  state: location.state,
                  stateCode,
                  latitude,
                  longitude
                });
                
                // Set as selected state for filtering
                console.log('🔍 Frontend: Setting selectedStateCode from geolocation:', stateCode);
                setSelectedStateCode(stateCode);
              } catch (error) {
                console.error('Reverse geocoding failed:', error);
                await detectLocationByIP();
              }
            },
            async (error) => {
              console.warn('Geolocation permission denied or failed:', error);
              await detectLocationByIP();
            },
            {
              timeout: 10000,
              enableHighAccuracy: false
            }
          );
        } else {
          console.warn('Geolocation not supported');
          await detectLocationByIP();
        }
      } catch (error) {
        console.error('Location detection failed:', error);
        setLocationError('Unable to detect your location. Please select a state from the sidebar.');
      } finally {
        setLocationLoading(false);
      }
    };

    // If loaderData has a state code, use that; otherwise detect location
    if (loaderData.state) {
      const stateCode = loaderData.state.toUpperCase();
      console.log('🔍 Frontend: Setting selectedStateCode from loaderData:', stateCode);
      setSelectedStateCode(stateCode);
      setLocationLoading(false);
    } else {
      detectUserLocation();
    }
  }, [loaderData.state]);

  // IP-based location detection fallback
  const detectLocationByIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      const stateCode = data.region || 'VA'; // Fallback to VA
      const stateName = getStateName(stateCode);
      
      const location = {
        city: data.city || 'Virginia Beach',
        state: stateName,
        stateCode,
        latitude: data.latitude,
        longitude: data.longitude
      };
      
      setUserLocation(location);
      console.log('🔍 Frontend: Setting selectedStateCode from IP:', stateCode);
      setSelectedStateCode(stateCode);
    } catch (error) {
      console.error('IP-based location detection failed:', error);
      setLocationError('Unable to detect your location. Please select a state from the sidebar.');
    }
  };

  // Reverse geocoding function
  const reverseGeocode = async (lat: number, lng: number): Promise<{ city: string; state: string }> => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      );
      const data = await response.json();
      
      return {
        city: data.city || data.locality || 'Virginia Beach',
        state: data.principalSubdivision || 'Virginia'
      };
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      throw error;
    }
  };

  // Process locations to get top city locations for sidebar
  useEffect(() => {
    if (locations.length > 0) {
      const cityLocations = locations
        .filter(location => location.city && location.state)
        .map(location => ({
          name: location.city!,
          state: location.state,
          stateCode: getStateCode(location.state),
          count: location.count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20); // Top 20 city locations

      setTopCityLocations(cityLocations);
    }
  }, [locations]);

  // Build filters - only use location if we have a valid state code
  const buildFilters = useCallback(() => {
    const filters: any = {
      page: currentPage,
      limit: itemsPerPage
    };

    // Use selectedStateCode if available
    if (selectedStateCode) {
      filters.state = selectedStateCode; // This will be the 2-digit code like "VA"
      
      // Add city filter from search params if present
      if (searchParams.get('city')) {
        filters.city = searchParams.get('city');
      }
    }

    // Add other filters from search params
    if (searchParams.get('category')) {
      filters.category = searchParams.get('category');
    }

    if (searchParams.get('search')) {
      filters.search = searchParams.get('search');
    }

    console.log('🔍 Frontend buildFilters result:', filters);

    return filters;
  }, [currentPage, selectedStateCode, searchParams]); // Added searchParams to dependencies

  // Get contractors with location-based filtering
  const filters = buildFilters();
  const {
    contractors,
    loading,
    error,
    pagination
  } = useContractors(
    filters, // Use the filters directly
    !!selectedStateCode // Only enable the hook if we have a state code
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLocationSelect = (stateCode: string, city?: string) => {
    setSelectedStateCode(stateCode);
    setCurrentPage(1);
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('state', stateCode);
    if (city) {
      newParams.set('city', city);
    } else {
      newParams.delete('city');
    }
    newParams.delete('page'); // Reset to first page
    
    // Actually update the search params to trigger re-render
    setSearchParams(newParams);
    
    // Update page title
    const stateName = getStateName(stateCode);
    setPageTitle(city ? `Contractors in ${city}, ${stateName}` : `Contractors in ${stateName}`);
    
    console.log('🔍 Frontend: Location selected:', { stateCode, city, newParams: newParams.toString() });
  };

  // Update page title based on selected location
  useEffect(() => {
    if (selectedStateCode) {
      const stateName = getStateName(selectedStateCode);
      const city = searchParams.get('city');
      setPageTitle(city ? `Contractors in ${city}, ${stateName}` : `Contractors in ${stateName}`);
    } else {
      setPageTitle('Contractors Directory');
    }
  }, [selectedStateCode, searchParams]);

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <SEOTags
          title={pageTitle}
          description={`Find qualified contractors in your area. Browse our directory of professional contractors.`}
          schemaType="ItemList"
        />

        <Container fluid>
          <AutoBreadCrumb />

          <Row>
            <Col lg={9}>
              {/* Location Detection Status */}
              {locationLoading && (
                <Card className="mb-3">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Detecting your location...</span>
                    </div>
                  </CardBody>
                </Card>
              )}

              {locationError && (
                <Alert color="warning" className="mb-3">
                  <h6 className="alert-heading">Location Required</h6>
                  {locationError}
                </Alert>
              )}

              {userLocation && (
                <Alert color="info" className="mb-3">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  Showing contractors near {userLocation.city}, {userLocation.state}
                  <Button 
                    color="link" 
                    size="sm" 
                    className="p-0 ms-2"
                    onClick={() => setLocationError('Please select a location from the sidebar.')}
                  >
                    Change Location
                  </Button>
                </Alert>
              )}

              {/* Main Content */}
              {!selectedStateCode ? (
                <Card>
                  <CardBody className="text-center py-5">
                    <i className="fas fa-map-marker-alt text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                    <h5>Select Your Location</h5>
                    <p className="text-muted">
                      Please select a state from the sidebar to view contractors in your area.
                    </p>
                  </CardBody>
                </Card>
              ) : (
                <>
                  <ContractorListingGrid 
                    contractors={contractors}
                    loading={loading}
                  />
                  
                  {pagination && pagination.totalCount > 0 && (
                    <ListingPagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(pagination.totalCount / itemsPerPage)}
                      onPageChange={handlePageChange}
                    />
                  )}

                  {!loading && pagination && pagination.totalCount === 0 && (
                    <Card>
                      <CardBody className="text-center py-5">
                        <i className="fas fa-search text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                        <h5>No Contractors Found</h5>
                        <p className="text-muted">
                          No contractors found in the selected location. Try selecting a different area.
                        </p>
                      </CardBody>
                    </Card>
                  )}
                </>
              )}
            </Col>

            {/* Right Sidebar - Top City Locations */}
            <Col lg={3}>
              <Card className="sticky-top" style={{ top: '20px' }}>
                <CardBody>
                  <h5>
                    <i className="fas fa-fire text-danger me-2"></i>
                    Popular Locations
                  </h5>
                  
                  {topCityLocations.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {topCityLocations.map((location, index) => (
                        <button
                          key={index}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            searchParams.get('city') === location.name && searchParams.get('state') === location.stateCode ? 'active' : ''
                          }`}
                          onClick={() => handleLocationSelect(location.stateCode, location.name)}
                        >
                          <div>
                            <div className="fw-medium">{location.name}</div>
                            <small className={searchParams.get('city') === location.name && searchParams.get('state') === location.stateCode ? 'text-white-50' : 'text-muted'}>
                              {location.state}
                            </small>
                          </div>
                          <Badge 
                            color={searchParams.get('city') === location.name && searchParams.get('state') === location.stateCode ? 'light' : 'primary'}
                            pill
                            className={searchParams.get('city') === location.name && searchParams.get('state') === location.stateCode ? 'text-dark' : ''}
                          >
                            {location.count}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2 mb-0">Loading locations...</p>
                    </div>
                  )}

                  {/* Additional State Selection */}
                  <div className="mt-3 pt-3 border-top">
                    <h6>Browse by State</h6>
                    <div className="row g-1">
                      {Object.entries(STATE_CODE_MAP).slice(0, 10).map(([name, code]) => (
                        <div key={code} className="col-6">
                          <Button
                            color={selectedStateCode === code ? 'primary' : 'outline-secondary'}
                            size="sm"
                            className="w-100 text-truncate"
                            onClick={() => handleLocationSelect(code)}
                          >
                            {code}
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="text-center mt-2">
                      <Link to="/contractors/locations" className="btn btn-link btn-sm">
                        View All States
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorListingPage;